import {
  SegmentedControl,
  SegmentedControlItem,
  Spacer,
  Stack,
  TextBody,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { FC } from 'react';
import { OrgMenuFilterMode } from './permissions-table-filter';
import { RolesTableOrgCounts } from './use-roles-table-org-filter';

/**
 * The order the segments are laid out in: the Org menu's own My, All, with
 * Hidden last because only this table has it (PSTUDIO-3330 review).
 */
export const ROLES_TABLE_FILTER_SEGMENTS = ['my', 'all', 'hidden'] as const;

const SEGMENT_LABEL_KEYS = {
  my: 'manager/common:permissionAndAccessTable.filterMyOrgs',
  all: 'manager/common:permissionAndAccessTable.filterAllOrgs',
  hidden: 'manager/common:permissionAndAccessTable.filterHiddenOrgs',
} as const;

const SEGMENT_FALLBACKS: Record<OrgMenuFilterMode, (count: number) => string> =
  {
    my: (count) => `My Orgs ${count}`,
    all: (count) => `All Orgs ${count}`,
    hidden: (count) => `Hidden Orgs ${count}`,
  };

type RolesTableOrgFilterProps = {
  mode: OrgMenuFilterMode;
  counts: RolesTableOrgCounts;
  onChange: (mode: OrgMenuFilterMode) => void;
};

/** The caption and the My / All / Hidden control above the roles table. */
export const RolesTableOrgFilter: FC<RolesTableOrgFilterProps> = ({
  mode,
  counts,
  onChange,
}) => {
  const [t] = useTranslation();

  return (
    <>
      <Spacer size="2xsmall" />
      <Stack alignItems="center" justifyContent="space-between" gutter="medium">
        <TextBody
          variant="tertiary"
          size="small"
          i18nKey="manager/common:permissionAndAccessTable.favoritesCaption"
        >
          Star an Org to add it to My Orgs. This doesn&apos;t change your
          access.
        </TextBody>
        <SegmentedControl
          value={mode}
          // `ps-segmented-control` sets `role="radiogroup"` on its own host —
          // it never slots content needing a shadow boundary workaround — so a
          // plain `aria-label` prop here reaches it directly (PSTUDIO-3330
          // review round 2): without one a screen reader announced an unnamed
          // "radio group".
          aria-label={
            t('manager/common:permissionAndAccessTable.filterOrgsAriaLabel') ||
            'Filter organizations'
          }
          onChange={(e) => onChange(e.detail.value as OrgMenuFilterMode)}
        >
          {ROLES_TABLE_FILTER_SEGMENTS.map((segment) => (
            <SegmentedControlItem key={segment} value={segment}>
              {t(SEGMENT_LABEL_KEYS[segment], { count: counts[segment] }) ||
                SEGMENT_FALLBACKS[segment](counts[segment])}
            </SegmentedControlItem>
          ))}
        </SegmentedControl>
      </Stack>
    </>
  );
};
